import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {catchError, combineLatest, distinctUntilChanged, EMPTY, filter, interval, map, Observable, Subscription, take, tap, timer} from "rxjs";
import {GameInstanceService} from "../../../services/game-instance.service";
import {IDrawNumberResponse} from "../../../interfaces/IDrawNumberResponse";
import {IEligibleTicketWithDrawDetails} from "../../../interfaces/IEligibleTicketWithDrawDetails";
import {GameService} from "../../../services/game.service";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {GameInstanceStatuses} from "../../../enum/GameInstanceStatusEnum";
import {IActiveGameInstance} from "../../../interfaces/IActiveGameInstance";
import {IDrawNumberRequest} from "../../../interfaces/IDrawNumberRequest";
import {ActiveUserService} from "../../../services/active-user.service";
import {switchMap} from "rxjs/operators";
import {AsyncPipe} from "@angular/common";
import {MatCard} from "@angular/material/card";
import {MatButton} from "@angular/material/button";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {PipesModule} from "../../../pipes/pipes.module";
import {ContainerComponent} from "@coreui/angular";
import {GameInstanceEntryStateTypes} from "../../../enum/GameInstanceEntryStateTypes";

@Component({
  selector: 'app-draw-winner',
  standalone: true,
  templateUrl: './draw-winner-page.component.html',
  imports: [
    AsyncPipe,
    MatCard,
    MatButton,
    MatProgressSpinner,
    PipesModule,
    ContainerComponent
  ],
  styleUrls: ['./draw-winner-page.component.scss']
})
export class DrawWinnerPageComponent implements OnInit, OnDestroy {

  private gameInstanceService: GameInstanceService = inject(GameInstanceService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private gameService: GameService = inject(GameService);

  public endDatePassedButNotReadyMessage: string = 'The game is now over! Just give us a couple of minutes to get ready for the draw!';
  public showEndDatePassedButNotReadyMessage: boolean = false;
  public winnerIsBeingDrawn: boolean = false;
  public drawClosedSub: Subscription | null = null;
  public activeGameInstance$: Observable<IActiveGameInstance> = this.gameInstanceService.selectActiveGameInstance();
  public winnerResponse?: IEligibleTicketWithDrawDetails;
  public winnerPlayed: boolean = false;
  public checkForGameReadyForDraw$: Observable<number> = interval(5000);
  public gameInstanceStates = GameInstanceStatuses;
  public uiState: UIStateEnum = UIStateEnum.ShowData;
  public uiStateForTemplate = UIStateEnum;
  protected readonly GameInstanceEntryStateTypes = GameInstanceEntryStateTypes;

  public activeGameName$: Observable<string> = this.gameService.selectActiveGame$().pipe(
    map(activeGame => activeGame?.Name || '')
  );

  ngOnInit() {
    this.watchForThisDrawToClose();
  }

  // Cross-game Draw Winner keeps this component when the admin picks another game, so each pick starts a fresh watch.
  private watchForThisDrawToClose() {
    this.drawClosedSub = this.gameService.selectActiveGameId().pipe(
      distinctUntilChanged(),
      tap(() => this.showEndDatePassedButNotReadyMessage = false),
      switchMap(gameIdP => gameIdP ? this.closeThenReadyForDraw(gameIdP) : EMPTY)
    ).subscribe();
  }

  private closeThenReadyForDraw(gameIdP: string): Observable<IActiveGameInstance> {
    return combineLatest([this.activeGameInstance$, timer(0, 1000)]).pipe(
      map(([drawP]) => drawP),
      filter(drawP => drawP?.GameId === gameIdP
        && drawP.State === GameInstanceStatuses.Active
        && new Date(drawP.EndedOn).getTime() <= Date.now()),
      take(1),
      tap(() => this.showEndDatePassedButNotReadyMessage = true),
      switchMap(() => this.checkForGameReadyForDraw$.pipe(
        // One failed poll is skipped; the next tick tries again.
        switchMap(() => this.gameInstanceService.activeGameInstanceByGame(gameIdP, this.activeUserService.activeUser().Id)
          .pipe(catchError(() => EMPTY))),
        filter(drawP => drawP.State == GameInstanceStatuses.ReadyForDraw),
        take(1))),
      tap(() => this.showEndDatePassedButNotReadyMessage = false)
    );
  }

  drawWinner() {
    this.winnerIsBeingDrawn = true;
    this.uiState = UIStateEnum.ShowLoading;
    const drawRequest: IDrawNumberRequest = {
      GameInstanceId: this.gameInstanceService.activeGameInstanceId(),
      AdminId: this.activeUserService.activeUser().Id,
      GameId: this.gameService.activeGame().Id
    };

    this.gameInstanceService.drawWinner(drawRequest).subscribe({
      next: (responseP: IDrawNumberResponse) => {
        if (responseP.GameInstanceWinnerDetails) {
          this.winnerIsBeingDrawn = false;
          this.winnerResponse = responseP.GameInstanceWinnerDetails;
          this.winnerPlayed = responseP.TicketWasPlayed;
          this.uiState = UIStateEnum.ShowData;
        }
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.winnerIsBeingDrawn = false;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    })
  }

  ngOnDestroy() {
    this.drawClosedSub?.unsubscribe();
  }
}
